import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export interface CustomerInvoice {
  id: string;
  customer_id: string;
  number: string | null;
  status: string;
  currency: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Invoices for ONE account (billing is always per account, never portfolio).
 * Backed by invoices_customer_view, which strips the raw source payload.
 * Source of record is Zoho Billing (sync scoped in docs/zoho-billing-invoices-scope.md).
 */
export function useCustomerInvoices(customerId: string | null) {
  const scope = useCustomerScope();
  const inScope = !!customerId && scope.ids.includes(customerId);

  return useQuery({
    queryKey: ['customer-invoices', scope.ownerKey, customerId],
    enabled: scope.ready && inScope,
    queryFn: async (): Promise<CustomerInvoice[]> => {
      const { data, error } = await supabase
        .from('invoices_customer_view')
        .select(
          'id, customer_id, number, status, currency, amount_due_cents, amount_paid_cents, amount_remaining_cents, issued_at, due_at, paid_at, hosted_invoice_url, invoice_pdf_url, description, created_at, updated_at',
        )
        .eq('customer_id', customerId!)
        .order('issued_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as CustomerInvoice[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
